/**
 * Derive usage bracket key and label from a price (for products with multiple usage variants).
 * Used to show an optional "Capacity" / "Variant" dropdown when a product has multiple brackets.
 */

/**
 * @param {Object} price - Price object with metadata (description, usage_limits, nickname) and lookup_key
 * @returns {{ key: string, label: string } | null} - Bracket key and display label, or null if not derivable
 */
export function getUsageBracketFromPrice(price) {
  if (!price) return null;
  const meta = price.metadata || {};
  if (!meta.usage_limits || typeof meta.usage_limits !== 'string') return null;
  try {
    const limits = JSON.parse(meta.usage_limits);
    if (typeof limits.max_storage_gb === 'number') {
      const gb = limits.max_storage_gb;
      const key = `storage_${gb}`;
      const label = gb >= 1024 ? `${gb / 1024} TiB` : `${gb} GiB`;
      return { key, label };
    }
    if (typeof limits.max_storage_mb === 'number') {
      const gb = Math.round(limits.max_storage_mb / 1024);
      const key = gb <= 0 ? 'storage_0' : `storage_${gb}`;
      const label = gb <= 0 ? 'Base (500 MB)' : (gb >= 1024 ? `${gb / 1024} TiB` : `${gb} GB`);
      return { key, label };
    }
    return null;
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[usageBrackets] Failed to parse usage_limits:', price?.metadata?.usage_limits, e);
    }
    return null;
  }
}

/**
 * @param {Array<Object>} prices - List of price objects
 * @returns {Array<{ key: string, label: string }>} - Unique usage brackets, sorted (storage numerically, else by label)
 */
export function getUsageBracketsFromPrices(prices) {
  if (!prices?.length) return [];
  const seen = new Set();
  const brackets = [];

  for (const price of prices) {
    const bracket = getUsageBracketFromPrice(price);
    if (bracket && !seen.has(bracket.key)) {
      seen.add(bracket.key);
      brackets.push(bracket);
    }
  }

  const hasStorageGb = brackets.some((b) => b.key.startsWith('storage_'));
  if (hasStorageGb) {
    brackets.sort((a, b) => {
      const numA = parseInt(a.key.replace('storage_', ''), 10) || 0;
      const numB = parseInt(b.key.replace('storage_', ''), 10) || 0;
      return numA - numB;
    });
  } else {
    brackets.sort((a, b) => (a.label || a.key).localeCompare(b.label || b.key));
  }

  if (process.env.NODE_ENV !== 'production' && prices.length > 0) {
    const keys = brackets.map((b) => b.key).join(', ');
    console.log('[usageBrackets] Derived', brackets.length, 'brackets from', prices.length, 'prices:', keys || '(none)');
  }
  return brackets;
}
